<template>
  <div v-if="isVisible" class="modal-backdrop">
    <div class="modal-content">
      <h3>{{ $t("judge.invitation.invite_modal_title") }}</h3>

      <!-- Buscador de Jueces -->
      <div class="filters-row">
        <div class="search-bar form-group">
          <div class="input-group">
            <span class="input-group-text"><i class="fa fa-search"></i></span>
            <input v-model="searchQuery" type="text" class="form-control" :placeholder="$t('judge.invitation.search_placeholder')" />
          </div>
        </div>

        <div class="filter-bar form-group">
          <div class="input-group">
            <span class="input-group-text"><i class="fa fa-flag"></i></span>
            <select v-model="countryFilter" class="form-select">
              <option value="">{{ allCountriesLabel }}</option>
              <option v-for="country in availableCountries" :key="country" :value="country">
                {{ countryFlag(country) }} {{ country }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Lista de atletas con scroll -->
      <div class="judge-list">
        <label v-for="judge in filteredJudges" :key="judge.id" class="judge-item">
          <input type="checkbox" v-model="selectedJudges" :value="judge.id" />
          <img :src="judge.img_path" alt="judge.name" class="judge-img" />
          <div class="judge-meta">
            <span class="judge-name">
              {{ judge.name }} {{ judge.surname }}
              <i v-if="isTechOperator(judge)" class="fa-solid fa-video tech-icon" aria-hidden="true"></i>
            </span>
            <div class="judge-row" v-if="extractCountryCode(judge) || hasJudgeStats(judge)">
              <span v-if="extractCountryCode(judge)" class="judge-country">
                {{ countryFlag(extractCountryCode(judge)) }}
              </span>
              <span class="judge-stats" v-if="hasJudgeStats(judge)">
                {{ judgeCompetitions(judge) }} · {{ judgeRounds(judge) }}
              </span>
            </div>
          </div>
        </label>
      </div>

      <!-- Botones de acción -->
      <div class="modal-buttons">
        <button @click="confirmSelection" class="btn btn-primary">{{ $t('competitions.btn_confirm') }}</button>
        <button @click="closeModal" class="btn btn-secondary">{{ $t('competitions.btn_cancel') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InviteJudgeModal",
  props: {
    isVisible: Boolean,
    judges: Array,
    competition: Object,
  },
  data() {
    return {
      selectedJudges: [],
      email: "",
      searchQuery: "",
      countryFilter: "",
    };
  },
  computed: {
    availableCountries() {
      const codes = new Set();
      (this.judges || []).forEach(judge => {
        const code = this.extractCountryCode(judge);
        if (code) codes.add(code);
      });
      return Array.from(codes).sort();
    },
    allCountriesLabel() {
      return "All countries";
    },
    filteredJudges() {
      if (!this.judges || !this.competition || !this.competition.competition_users) {
        return [];
      }

      // Excluir solo a quienes ya están como jueces en esta competición (role_id 2)
      const judgeUserIds = new Set(
          this.competition.competition_users
              .filter(user => user.role_id === 2)
              .map(user => user.user.id)
      );

      return this.judges.filter(judge => {
        const fullName = `${judge.name} ${judge.surname}`.toLowerCase();
        const email = (judge.email || '').toLowerCase();
        const query = this.searchQuery.toLowerCase();
        const matchesSearch = !query || fullName.includes(query) || email.includes(query);
        const countryCode = this.extractCountryCode(judge);
        const matchesCountry = !this.countryFilter || countryCode === this.countryFilter;
        const notAlreadyJudge = !judgeUserIds.has(judge.id);

        return matchesSearch && matchesCountry && notAlreadyJudge;
      });
    }
  },
  methods: {
    closeModal() {
      this.selectedJudges = [];
      this.$emit("closeModal");
    },
    confirmSelection() {
      this.$emit("inviteJudges", this.selectedJudges);
      this.closeModal();
    },
    isTechOperator(judge) {
      return Boolean(
        judge?.certification?.tech_operator ||
        judge?.active_judge_certification?.tech_operator
      );
    },
    extractCountryCode(judge) {
      const code = judge?.country || judge?.stats?.country_code;
      return code ? String(code).toUpperCase() : "";
    },
    countryFlag(code) {
      if (!code || code.length !== 2) return "🏳️";
      const base = 127397;
      const chars = code.toUpperCase().split("").map(c => base + c.charCodeAt(0));
      return String.fromCodePoint(...chars);
    },
    judgeCompetitions(judge) {
      const count = Number(judge?.stats?.competitions_count || 0);
      return `Comp: ${count}`;
    },
    judgeRounds(judge) {
      const count = Number(judge?.stats?.rounds_scored_count || 0);
      return `Rounds: ${count}`;
    },
    hasJudgeStats(judge) {
      return Number.isFinite(Number(judge?.stats?.competitions_count)) || Number.isFinite(Number(judge?.stats?.rounds_scored_count));
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  width: 50%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.email-invite, .search-bar {
  margin-bottom: 15px;
}
.filters-row {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}
.filters-row .search-bar {
  flex: 1;
  margin-bottom: 0;
}
.filters-row .filter-bar {
  width: 200px;
  margin-bottom: 0;
}
.email-list {
  text-align: left;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 5px;
  margin-bottom: 10px;
}
.email-list ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}
.email-list li {
  padding: 5px 0;
  font-size: 14px;
}
.judge-list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 50vh;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
}
.judge-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding-bottom: 15px;
}
.judge-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  text-align: left;
}
.judge-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.judge-name {
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.judge-country {
  font-size: 12px;
  color: #555;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: "Twemoji Mozilla", "Noto Color Emoji", "Segoe UI Emoji", "Apple Color Emoji", "Segoe UI", system-ui, sans-serif;
}
.judge-stats {
  font-size: 12px;
  color: #666;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tech-icon {
  color: #1b5bbf;
  font-size: 0.9em;
}
.judge-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}
.modal-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}
.btn {
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.btn-primary {
  background: #28a745;
  color: white;
}
.btn-secondary {
  background: #ccc;
}
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
  }

  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filters-row .search-bar,
  .filters-row .filter-bar {
    width: 100%;
  }
}
</style>